import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import { categories, getComicsByCategory, getCategoryBySlug } from '@/mocks/comics';

function CategoryPage() {
  const { slug = 'all' } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug);
  const [sortBy, setSortBy] = useState<'default' | 'rating' | 'views' | 'newest'>('default');

  useEffect(() => {
    setActiveSlug(slug);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const category = getCategoryBySlug(activeSlug);
  const comics = getComicsByCategory(activeSlug);

  const sortedComics = [...comics].sort((a, b) => {
    switch (sortBy) {
      case 'rating':
        return b.rating - a.rating;
      case 'views':
        return b.views - a.views;
      case 'newest':
        return new Date(b.updateTime).getTime() - new Date(a.updateTime).getTime();
      default:
        return 0;
    }
  });

  const catName = category?.name || '全部';
  const catDesc = category?.description || '所有韩漫作品一览';

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${catName} - 韩漫漫画网`,
    description: `${catDesc}，韩漫漫画网为您精选${catName}类热门漫画，全网最快更新，极速阅读体验。`,
    url: `https://hanman.com/category/${activeSlug}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: sortedComics.map((comic, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'CreativeWork',
          name: comic.title,
          url: `https://hanman.com/article/${comic.id}`,
        },
      })),
    },
  };

  return (
    <>
      <SEOHead
        title={`${catName}漫画 - 韩漫漫画网`}
        description={`${catDesc}，韩漫漫画网为您精选${catName}类热门韩漫，涵盖恋爱、奇幻、动作、悬疑等多种题材，全网最快更新，极速阅读体验。`}
        keywords={`${catName}韩漫,${catName}漫画,韩漫在线阅读,热门漫画,最快更新`}
        canonicalUrl={`/category/${activeSlug}`}
        schema={categorySchema}
      />

      <main className="w-full bg-background-50 min-h-screen">
        <div className="bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6 pt-6 pb-6">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '漫画分类', path: '/category/all' },
                { label: catName },
              ]}
            />
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mt-4" style={{ fontFamily: 'var(--font-heading)' }}>
              {catName}漫画
            </h1>
            <p className="text-sm text-foreground-600 mt-1">{catDesc}</p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <div className="flex flex-wrap items-center gap-2 mb-6">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activeSlug === cat.slug
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-700 hover:bg-primary-100/70'
                }`}
              >
                {cat.name}
                <span className="ml-1 text-xs opacity-70">({cat.count})</span>
              </Link>
            ))}
          </div>

          <div className="flex items-center justify-between mb-5">
            <p className="text-sm text-foreground-600">
              共找到 <strong className="text-foreground-950">{sortedComics.length}</strong> 部漫画
            </p>
            <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
              {([
                { key: 'default', label: '默认' },
                { key: 'rating', label: '评分' },
                { key: 'views', label: '人气' },
                { key: 'newest', label: '最新' },
              ] as const).map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setSortBy(opt.key)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                    sortBy === opt.key
                      ? 'bg-background-50 text-foreground-950'
                      : 'text-foreground-600 hover:text-foreground-950'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {sortedComics.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5" data-product-shop>
              {sortedComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} size="small" />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
                <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-600">该分类暂无漫画，请查看其他分类</p>
              <Link
                to="/category/all"
                className="inline-flex items-center gap-2 mt-4 text-primary-500 hover:text-primary-600 text-sm font-medium cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i> 查看全部漫画
              </Link>
            </div>
          )}
        </div>
      </main>
    </>
  );
}

export default CategoryPage;